import React, {useCallback, useContext, useEffect, useRef} from 'react'
import {Dialog} from './dialog'
import _ from './dialog.module.scss'
import {DialogCloseContext, DialogContext} from './dialog-context'

export const DialogOutlet = () => {
    const context = useContext(DialogContext)
    const outletRef = useRef(null)
    const closeHandlersRef = useRef(new Map())

    useEffect(() => {
        if (!context?.dialogs.length) return

        const bodyOverflow = document.body.style.overflow
        const htmlOverflow = document.documentElement.style.overflow
        document.body.style.overflow = 'hidden'
        document.documentElement.style.overflow = 'hidden'

        const outletElement = outletRef.current
        const isInsideDialog = event => outletRef.current?.contains(event.target)
        const blockEvent = (event, preventDefault = true) => {
            if (preventDefault) event.preventDefault()
            event.stopPropagation()
            event.stopImmediatePropagation?.()
        }
        const blockBackgroundEvent = event => {
            if (event.type === 'keydown' && event.key === 'Escape') {
                const dialogConfig = context.dialogs.at(-1)
                if (dialogConfig?.closeOnEscape !== false) {
                    const close = closeHandlersRef.current.get(dialogConfig.id)
                    if (close) close()
                    else {
                        dialogConfig.onClose?.()
                        context.close(dialogConfig.id)
                    }
                }
                blockEvent(event)
                return
            }
            if (!isInsideDialog(event)) blockEvent(event, event.type !== 'keydown')
        }
        const stopInsideEvent = event => {
            if (isInsideDialog(event)) event.stopPropagation()
        }

        window.addEventListener('keydown', blockBackgroundEvent, true)
        window.addEventListener('wheel', blockBackgroundEvent, {capture: true, passive: false})
        window.addEventListener('touchmove', blockBackgroundEvent, {capture: true, passive: false})
        outletElement?.addEventListener('wheel', stopInsideEvent)
        outletElement?.addEventListener('touchmove', stopInsideEvent)

        return () => {
            document.body.style.overflow = bodyOverflow
            document.documentElement.style.overflow = htmlOverflow
            window.removeEventListener('keydown', blockBackgroundEvent, true)
            window.removeEventListener('wheel', blockBackgroundEvent, true)
            window.removeEventListener('touchmove', blockBackgroundEvent, true)
            outletElement?.removeEventListener('wheel', stopInsideEvent)
            outletElement?.removeEventListener('touchmove', stopInsideEvent)
        }
    }, [context])

    if (!context) return null

    return <div ref={outletRef} className={_.outlet}>
        {context.dialogs.map(dialogConfig => <DialogOutletItem key={dialogConfig.id} dialogConfig={dialogConfig}
            closeDialog={context.close} closeHandlersRef={closeHandlersRef}/>)}
    </div>
}
const DialogOutletItem = ({dialogConfig, closeDialog, closeHandlersRef}) => {
    const registerClose = useCallback(handler => {
        closeHandlersRef.current.set(dialogConfig.id, handler)
        return () => closeHandlersRef.current.delete(dialogConfig.id)
    }, [dialogConfig.id, closeHandlersRef])
    const handleClose = () => {
        dialogConfig.onClose?.()
        closeDialog(dialogConfig.id)
    }
    return <DialogCloseContext.Provider value={registerClose}>
        {dialogConfig.render ? dialogConfig.render({close: handleClose}) : <Dialog {...dialogConfig} onClose={handleClose}/>}
    </DialogCloseContext.Provider>
}
